import { useCallback, useEffect, useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import { Form } from 'react-final-form';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import EditUserForm from '../../components/reusable/editUserForm';
import FormControlledTextField from '../../components/reusable/FormControlledTextField';
import { changePassword, getAllLocations, updateUser } from '../../constants/api';
import { IApiErrorResponse, IApiLocation, IApiUser } from '../../constants/api-interfaces';
import { ChangePasswordFormValues, EditUserFormValues } from '../../constants/interfaces';
import { changePasswordValidation, editUserValidation } from '../../constants/validationSchemas';
import { useAuth } from '../../contexts/AuthContext';
import { useSnackbar } from '../../contexts/SnackbarContext';

const formatValue = (value: string | null | undefined) => (value === null || value === undefined || value === '' ? '—' : value);

const formatDateValue = (value: string | null | undefined) => {
  if (!value) {
    return '—';
  }

  return value.slice(0, 10);
};

export default function UserDetails() {
  const { auth, authDispatch } = useAuth();
  const { snackbarDispatch } = useSnackbar();
  const [locations, setLocations] = useState<IApiLocation[]>([]);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);

  const fetchLocations = useCallback(async () => {
    try {
      const locationsResponse = await getAllLocations();
      setLocations(locationsResponse.data.locations || []);
    } catch (error) {
      const axiosError = error as AxiosError<IApiErrorResponse>;
      snackbarDispatch({
        type: 'setSnackbar',
        payload: {
          message: axiosError.response?.data?.message || 'Unable to load locations.',
          error: true,
        },
      });
    }
  }, [snackbarDispatch]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const currentUser = auth?.user as IApiUser | undefined;

  const userRows = useMemo(() => {
    if (!currentUser) {
      return [] as Array<{ key: string; label: string; value: string }>;
    }

    return [
      { key: 'firstName', label: 'First Name', value: formatValue(currentUser.firstName) },
      { key: 'lastName', label: 'Last Name', value: formatValue(currentUser.lastName) },
      { key: 'email', label: 'Email', value: formatValue(currentUser.email) },
      { key: 'phoneNumber', label: 'Phone Number', value: formatValue(currentUser.phoneNumber) },
      { key: 'dateOfBirth', label: 'Date Of Birth', value: formatDateValue(currentUser.dateOfBirth) },
      { key: 'startDate', label: 'Start Date', value: formatDateValue(currentUser.startDate) },
      { key: 'role', label: 'Role', value: formatValue(currentUser.role) },
      { key: 'location', label: 'Location', value: formatValue(currentUser.location?.name) },
    ];
  }, [currentUser]);

  const initialFormValues = useMemo<EditUserFormValues>(() => {
    if (!currentUser) {
      return {
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        dateOfBirth: '',
        startDate: '',
        locationId: '',
      };
    }

    return {
      firstName: currentUser.firstName || '',
      lastName: currentUser.lastName || '',
      email: currentUser.email || '',
      phoneNumber: currentUser.phoneNumber || '',
      dateOfBirth: currentUser.dateOfBirth ? currentUser.dateOfBirth.slice(0, 10) : '',
      startDate: currentUser.startDate ? currentUser.startDate.slice(0, 10) : '',
      locationId: currentUser.locationId || '',
    };
  }, [currentUser]);

  const handleSelfUpdate = async (values: EditUserFormValues) => {
    if (!auth?.user?.id) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await updateUser({
        id: auth.user.id,
        email: values.email.trim() || undefined,
        firstName: values.firstName.trim() || undefined,
        lastName: values.lastName.trim() || undefined,
        phoneNumber: values.phoneNumber.trim() || undefined,
        dateOfBirth: values.dateOfBirth || undefined,
        startDate: values.startDate || undefined,
        locationId: values.locationId.trim() || undefined,
      });

      authDispatch({
        type: 'setAuth',
        payload: {
          success: true,
          user: response.data.user,
        },
      });

      snackbarDispatch({
        type: 'setSnackbar',
        payload: {
          message: 'Your account details were updated successfully.',
          error: false,
        },
      });
      setIsEditOpen(false);
    } catch (error) {
      const axiosError = error as AxiosError<IApiErrorResponse>;
      snackbarDispatch({
        type: 'setSnackbar',
        payload: {
          message: axiosError.response?.data?.message || 'Unable to update your account details.',
          error: true,
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLocationDisabled = auth?.user?.role === 'teammember';
  const isStartDateDisabled = auth?.user?.role === 'teammember';

  const initialPasswordValues: ChangePasswordFormValues = {
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  };

  const handleChangePasswordSubmit = async (values: ChangePasswordFormValues) => {
    setIsPasswordSubmitting(true);

    try {
      await changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });

      snackbarDispatch({
        type: 'setSnackbar',
        payload: {
          message: 'Password updated successfully.',
          error: false,
        },
      });

      setIsPasswordDialogOpen(false);
    } catch {
      snackbarDispatch({
        type: 'setSnackbar',
        payload: {
          message: 'Change request failed.',
          error: true,
        },
      });
    } finally {
      setIsPasswordSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 5,
        minHeight: '80vh',
        background: 'linear-gradient(180deg, rgba(25, 118, 210, 0.04) 0%, rgba(255, 255, 255, 0) 100%)',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 680,
          p: 3,
          borderRadius: 3,
          border: '1px solid rgba(25, 118, 210, 0.18)',
          boxShadow: '0 18px 42px rgba(15, 23, 42, 0.09)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <AccountCircleOutlinedIcon sx={{ color: 'primary.main', fontSize: 30 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                User Details
              </Typography>
            </Box>
          </Box>
          <IconButton
            aria-label="edit user details"
            size="small"
            color="primary"
            onClick={() => setIsEditOpen(true)}
            sx={{
              border: '2px solid',
              borderColor: 'primary.main',
              p: 0.75,
              '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' },
            }}
          >
            <EditOutlinedIcon />
          </IconButton>
        </Box>

        {userRows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No user details available.
          </Typography>
        ) : (
          <Grid container spacing={1.25}>
            {userRows.map(({ key, label, value }) => (
              <Grid size={{ xs: 12 }} key={key}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    border: '1px solid rgba(15, 23, 42, 0.08)',
                    borderRadius: 1.5,
                    px: 1.5,
                    py: 1.1,
                    backgroundColor: 'rgba(248, 250, 252, 0.7)',
                    gap: 2,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: 'text.secondary',
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ textAlign: 'right', wordBreak: 'break-word', fontWeight: 600 }}>
                    {value}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2.5 }}>
          <Button
            variant="contained"
            onClick={() => setIsPasswordDialogOpen(true)}
            sx={{ minWidth: 220 }}
          >
            Change Password
          </Button>
        </Box>
      </Paper>

      <Dialog
        open={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit your details</DialogTitle>
        <Form
          key={auth?.user?.id ?? 'self-user'}
          onSubmit={handleSelfUpdate}
          initialValues={initialFormValues}
          validate={editUserValidation}
          render={({ handleSubmit, form }) => (
            <form onSubmit={handleSubmit} noValidate>
              <DialogContent sx={{ pt: 1 }}>
                <EditUserForm
                  form={form}
                  locations={locations}
                  isEmailDisabled={false}
                  isLocationDisabled={isLocationDisabled}
                  isStartDateDisabled={isStartDateDisabled}
                />
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 3, gap: 1.25, justifyContent: 'center' }}>
                <Button
                  onClick={() => setIsEditOpen(false)}
                  variant="outlined"
                  color="error"
                  disabled={isSubmitting}
                  sx={{ width: { xs: '46%', md: '46%', lg: 'auto' }, minWidth: { xs: 96, lg: 120 } }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                  sx={{ width: { xs: '46%', md: '46%', lg: 'auto' }, minWidth: { xs: 96, lg: 120 } }}
                >
                  Save
                </Button>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>

      <Dialog
        open={isPasswordDialogOpen}
        onClose={() => setIsPasswordDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change password</DialogTitle>
        <Form
          initialValues={initialPasswordValues}
          validate={changePasswordValidation}
          onSubmit={handleChangePasswordSubmit}
          render={({ handleSubmit }) => (
            <form onSubmit={handleSubmit} noValidate>
              <DialogContent sx={{ pt: 1 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <FormControlledTextField
                      name="currentPassword"
                      label="Current Password"
                      type="password"
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <FormControlledTextField
                      name="newPassword"
                      label="New Password"
                      type="password"
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <FormControlledTextField
                      name="confirmNewPassword"
                      label="Confirm New Password"
                      type="password"
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={() => setIsPasswordDialogOpen(false)} variant="outlined" color="error" disabled={isPasswordSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" color="primary" disabled={isPasswordSubmitting}>
                  Update Password
                </Button>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>
    </Box>
  );
}
